import React, { PureComponent } from 'react'
import { AggregationOptions } from '../aggregations'
import { RouteContext } from '../route-provider'
import {
  OnGroupToggleProps,
  OnOptionToggleProps
} from '../../legacy-design-system/filter'
import { MetadataFilter } from '../applications-grid'

/* this is a simple version of src/components/filter/filter-manager.tsx
 *  TODO types, interfaces and methods could possible be shared (this can be completed in a separated ticket)
 * */

export interface FilterManagerChildProps<TAggregationCategory extends string> {
  readonly onGroupToggle: (
    props: OnGroupToggleProps<TAggregationCategory>
  ) => void
  readonly onOptionToggle: (
    props: OnOptionToggleProps<TAggregationCategory>
  ) => void
  readonly aggregationSelections: AggregationOptions<TAggregationCategory>
  readonly onAggregationCategoriesClear: (
    categories: TAggregationCategory[]
  ) => void
  readonly resetFilters: () => void
  readonly onClearMetadataSelections: () => void
  readonly onMetadataOptionsToggle: (
    groupID: string,
    optionIDs: string[]
  ) => void
  readonly customMetadataSelections: MetadataFilter[]
}

type RouteType = 'applications'

type RoutePositionType = Record<number, RouteType | undefined>

type FilterTypeRoutePosition = Record<FilterType, RoutePositionType>

export const expectedRoutes: FilterTypeRoutePosition = {
  applications: { 1: 'applications', 2: undefined }
}

export type FilterType = 'applications'

interface ManagerProps<TAggregationCategory extends string> {
  readonly children: (
    props: FilterManagerChildProps<TAggregationCategory>
  ) => React.ReactNode
  readonly filterType: FilterType
  readonly customMetadataSelections: MetadataFilter[]
}

export class FilterManager<
  TAggregationCategory extends string
> extends PureComponent<ManagerProps<TAggregationCategory>> {
  constructor(props: ManagerProps<TAggregationCategory>) {
    super(props)
  }

  public render() {
    return (
      <RouteContext.Consumer>
        {({
          queryParameters,
          setQueryParameter,
          toggleQueryParameter,
          clear,
          applyExisitingFilters,
          clearAll,
          parameters,
          updateQueryParameters
        }) => {
          const { filterType, customMetadataSelections, children } = this.props
          return (
            <FilterManagerContent
              children={children}
              queryParameters={queryParameters}
              setQueryParameter={setQueryParameter}
              toggleQueryParameter={toggleQueryParameter}
              clear={clear}
              updateQueryParameters={updateQueryParameters}
              routeParameters={parameters}
              applyExisitingFilters={applyExisitingFilters}
              clearAll={clearAll}
              filterType={filterType}
              customMetadataSelections={customMetadataSelections}
            />
          )
        }}
      </RouteContext.Consumer>
    )
  }
}

interface FilterManagerContentProps<TAggregationCategory extends string> {
  readonly children: (
    props: FilterManagerChildProps<TAggregationCategory>
  ) => React.ReactNode

  readonly filterType: FilterType
  readonly queryParameters: Map<string, string[]>
  readonly setQueryParameter: (
    filtertype: FilterType,
    parameter: string,
    value: string | string[]
  ) => void
  readonly toggleQueryParameter: (
    filtertype: FilterType,
    parameter: string,
    value: string
  ) => void
  readonly clear: (
    filtertype: FilterType,
    parameters: string[] | string
  ) => void
  readonly clearAll: (filtertype: FilterType) => void
  readonly applyExisitingFilters: (filtertype: FilterType) => void
  readonly updateQueryParameters: (
    filterType: FilterType,
    newParameters: Map<string, string[]>
  ) => void
  readonly routeParameters: string[]
  readonly customMetadataSelections: MetadataFilter[]
}

interface FilterManagerContentState<TAggregationCategory extends string> {
  readonly aggregationOptions?: AggregationOptions<TAggregationCategory>
  readonly customMetadataSelections: MetadataFilter[]
}

export class FilterManagerContent<
  TAggregationCategory extends string
> extends PureComponent<
  FilterManagerContentProps<TAggregationCategory>,
  FilterManagerContentState<TAggregationCategory>
> {
  constructor(props: FilterManagerContentProps<TAggregationCategory>) {
    super(props)

    this.state = {
      customMetadataSelections: props.customMetadataSelections
    }
  }

  public componentDidMount() {
    const current = this.props.queryParameters

    if (
      current.size > 0 &&
      this.routeMatchesExpected(
        this.props.routeParameters,
        this.props.filterType
      )
    ) {
      // If this component gets Mounted and there are filters in the URL
      // and no existing filters, then save the state
      this.props.updateQueryParameters(
        this.props.filterType,
        this.props.queryParameters
      )
    }
  }

  public componentDidUpdate(prevProps) {
    // If the route is updated to this filter manager
    // apply the existing filters

    if (
      this.routeChanged(
        prevProps.routeParameters,
        this.props.routeParameters
      ) &&
      this.routeMatchesExpected(
        this.props.routeParameters,
        this.props.filterType
      )
    ) {
      const current = this.props.queryParameters
      if (current.size === 0) {
        // if the route changed and there are no filters set in the URL,
        // then apply existing filters
        this.props.applyExisitingFilters(this.props.filterType)
      } else {
        // If the route changed and the there are filters in the URL,
        // then update the existing filters to match the new URL filters
        this.props.updateQueryParameters(
          this.props.filterType,
          this.props.queryParameters
        )
      }
    }
  }

  public routeMatchesExpected(
    routeParameters: string[],
    filterType: FilterType
  ): boolean {
    const expectedRoute = expectedRoutes[filterType]

    let routeMatchesExpected = true
    Object.keys(expectedRoute).forEach((key) => {
      if (routeMatchesExpected && routeParameters[key] !== expectedRoute[key]) {
        routeMatchesExpected = false
      }
    })

    return routeMatchesExpected
  }

  public routeChanged(routeParams1: string[], routeParams2: string[]): boolean {
    if (routeParams1.length !== routeParams2.length) {
      return true
    }

    for (let i = 0; i <= routeParams1.length; i++) {
      if (routeParams1[i] !== routeParams2[i]) {
        return true
      }
    }

    return false
  }

  public render() {
    const {
      filterType,
      queryParameters,
      setQueryParameter,
      toggleQueryParameter,
      clear,
      clearAll
    } = this.props

    const aggregationSelectionsMap: Map<string, string[]> = queryParameters
      ? queryParameters
      : new Map<string, string[]>()

    return this.props.children({
      aggregationSelections: this.convertAggregationsMap(
        aggregationSelectionsMap
          ? aggregationSelectionsMap
          : new Map<string, string[]>()
      ),
      customMetadataSelections: this.state.customMetadataSelections,
      onGroupToggle: ({
        groupID,
        optionsAvailableForSelection
      }: OnGroupToggleProps<TAggregationCategory>) => {
        const selected = queryParameters.get(groupID)

        const selectionsLength = selected ? selected.length : 0
        const availableLength = optionsAvailableForSelection.length
        const allSelected = selectionsLength === availableLength
        if (allSelected) {
          // Deselect all
          setQueryParameter(filterType, groupID, [])
        } else {
          // Select all
          setQueryParameter(filterType, groupID, optionsAvailableForSelection)
        }
      },
      onOptionToggle: ({
        groupID,
        optionID
      }: OnOptionToggleProps<TAggregationCategory>) => {
        toggleQueryParameter(filterType, groupID, optionID)
      },
      onAggregationCategoriesClear: (categories: TAggregationCategory[]) => {
        clear(filterType, categories)
      },
      resetFilters: () => {
        clearAll(filterType)
      },
      onMetadataOptionsToggle: (groupID: string, optionIDs: string[]) => {
        this.setState(({ customMetadataSelections: prevSelections }) => {
          const groupIndex = prevSelections.findIndex(
            ({ fieldID }) => fieldID === groupID
          )

          if (groupIndex > -1) {
            const selection = prevSelections[groupIndex]
            const updatedValues = optionIDs.reduce((acc, curr) => {
              const index = acc.indexOf(curr)
              if (index > -1) {
                return [
                  ...acc.slice(0, index),
                  ...acc.slice(index + 1, acc.length)
                ]
              }

              return [...acc, curr]
            }, selection.values)

            return {
              customMetadataSelections:
                updatedValues.length > 0
                  ? [
                      ...prevSelections.slice(0, groupIndex),
                      {
                        fieldID: groupID,
                        values: updatedValues
                      },
                      ...prevSelections.slice(
                        groupIndex + 1,
                        prevSelections.length
                      )
                    ]
                  : [
                      ...prevSelections.slice(0, groupIndex),
                      ...prevSelections.slice(
                        groupIndex + 1,
                        prevSelections.length
                      )
                    ]
            }
          }

          return {
            customMetadataSelections: [
              ...prevSelections,
              { fieldID: groupID, values: optionIDs }
            ]
          }
        })
      },
      onClearMetadataSelections: () => {
        this.setState({ customMetadataSelections: [] })
      }
    })
  }

  private convertAggregationsMap = (
    map
  ): AggregationOptions<TAggregationCategory> => {
    const selections: AggregationOptions<string> = {}

    map.forEach((value, key) => {
      selections[key] = value
    })

    return selections
  }
}
